/*
 * Copyright © 2018-2023, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { ReactElement, useCallback, useEffect, useState } from "react";
import { strings } from "../../../common/Strings/Strings";
import { getGeneralError } from "../../../util/helperFunctions";
import { CountryResponse } from "../../../models/management/CountryResponse";
import CountryApi from "../../../api/CountryApi";
import { InsuranceCompanyResponse } from "../../../models/insurance/InsuranceCompanyResponse";
import InsuranceApi from "../../../api/InsuranceApi";
import LoaderInline from "../../../components/LoaderInline";
import { SiteManagerPage } from "../SiteManagerPage";
import { PageProps } from "../../../models/PageProps";
import InsuranceCompanyDeletionModal from "./InsuranceCompanyDeletionModal";
import InsuranceCompanyEditorModal from "./InsuranceCompanyEditorModal";
import AlertBox from "../../../components/AlertBox";
import Button from "../../../components/Button";
import { PencilSquare } from "../../../common/Icons/PencilSquare";
import { Trash } from "../../../common/Icons/Trash";

function InsurancePage({ setPageLoading }: PageProps): ReactElement {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);

  const [isEditorModalVisible, setEditorModalVisible] =
    useState<boolean>(false);
  const [isDeletionModalVisible, setDeletionModalVisible] =
    useState<boolean>(false);

  const [companies, setCompanies] = useState<InsuranceCompanyResponse[]>([]);
  const [countries, setCountries] = useState<CountryResponse[]>([]);

  const [selectedCompany, setSelectedCompany] =
    useState<InsuranceCompanyResponse>();

  const getInsuranceCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await InsuranceApi.getInsuranceCompanies();
      setCompanies(resp.data);
    } catch (err) {
      setError(await getGeneralError(err));
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }, [setPageLoading]);

  const getCountries = async () => {
    try {
      const response = await CountryApi.getCountries();
      setCountries(response.data);
    } catch (err) {
      setError(await getGeneralError(err));
    }
  };

  useEffect(() => {
    void getInsuranceCompanies();
    void getCountries();
  }, [getInsuranceCompanies]);

  const openAddModal = () => {
    setEditorModalVisible(true);
    setSelectedCompany(undefined);
    setError(null);
  };

  const closeEditorModal = () => {
    setEditorModalVisible(false);
    setSelectedCompany(undefined);
    setError(null);
  };

  const openEditModal = (company: InsuranceCompanyResponse) => {
    setEditorModalVisible(true);
    setSelectedCompany(company);
    setError(null);
  };

  const openDeletionModal = (company: InsuranceCompanyResponse) => {
    setDeletionModalVisible(true);
    setSelectedCompany(company);
    setError(null);
  };

  const closeDeletionModal = () => {
    setDeletionModalVisible(false);
    setSelectedCompany(undefined);
    setError(null);
  };

  return (
    <>
      <LoaderInline className="my-3" hidden={!isLoading} />
      <div hidden={isLoading}>
          <div>
            <AlertBox message={error} className="mb-2" />
            <div className="tw-card p-4">
              <div className="p-4 flex">
                <div>
                  {strings.insuranceCompanies}
                </div>
                <div className="ml-auto">
                  <Button onClick={openAddModal}>
                    {strings.addNew}
                  </Button>
                </div>
              </div>
              <div>
                <table className="tw-table">
                  <thead className="tw-thead">
                    <tr>
                      <th className="tw-th">{strings.businessName}</th>
                      <th className="tw-th">{strings.country}</th>
                      <th className="tw-th"/>
                    </tr>
                  </thead>
                  <tbody className="tw-tbody">
                    {companies.map((company) => (
                      <tr>
                        <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">{company.name}</td>
                        <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                          {
                            countries?.find(
                              (i) => i.countryCode === company.countryCode
                            )?.displayName
                          }
                        </td>
                        <td className="px-4 py-3 text-sm font-medium whitespace-nowrap text-right">
                          <Button
                            className="mr-2"
                            variant="icon"
                            onClick={() => openEditModal(company)}
                          >
                            <PencilSquare className="w-5 h-5 flex-shrink-0" />
                          </Button>
                          <Button
                            variant="icon"
                            onClick={() => openDeletionModal(company)}
                          >
                            <Trash className="w-5 h-5 flex-shrink-0"/>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
      </div>
      <InsuranceCompanyEditorModal
        show={isEditorModalVisible}
        onHide={closeEditorModal}
        refresh={getInsuranceCompanies}
        countries={countries}
        selectedCompany={selectedCompany}
      />
      <InsuranceCompanyDeletionModal
        show={isDeletionModalVisible}
        onHide={closeDeletionModal}
        refresh={getInsuranceCompanies}
        selectedCompany={selectedCompany}
      />
    </>
  );
}

export default SiteManagerPage(InsurancePage, strings.INSURANCE);
